/**
 * CanvasGrid — absolutely-positioned report grid that replaces react-grid-layout
 * (Epic 37 canvas migration, Stages 3-5). Each widget is placed at its
 * {row,col,colSpan,rowSpan} cell via `cellToPixelRect` (the SAME math the server
 * PDF renderer uses, so editor ≡ PDF). dnd-kit drives MOVE; a custom SE handle
 * drives RESIZE. No compaction — widgets stay where placed (free vertical model).
 *
 * Why hand-rolled instead of RGL: RGL packs the grid (no free placement) and its
 * CSS-transform drop math drifts under our pan/zoom (documented bug). Positioning
 * widgets ourselves + transform-safe delta math (reportDnd) fixes both.
 *
 * Move: each widget is a dnd-kit useDraggable (source:"widget"). The live
 * translate follows the cursor (dnd-kit transform); on drag-end the provider
 * routes the delta to onWidgetMove, which snaps to a cell (applyMoveDelta).
 * Resize: pointer-drag on the SE handle, transform-safe (applyResizeDelta),
 * committing colSpan (snapped to valid) + rowSpan (free) on pointer-up.
 */

import { useRef, useState, useCallback, useEffect, type ReactNode } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Trash2, Copy } from "lucide-react";
import { snapToValidSpan, type ValidColSpan } from "../../lib/gridUtils";
import { cellToPixelRect, applyResizeDelta, type GridMetrics } from "../../lib/reportDnd";
import type { WidgetConfig } from "../../lib/templateUtils";

export interface CanvasGridProps {
  widgets: WidgetConfig[];
  metrics: GridMetrics;
  /** Total grid height (px, unscaled) — fills the page so drops land anywhere. */
  minHeightPx: number;
  scale: number;
  editable: boolean;
  selectedWidgetId?: string | null;
  /** The id currently being moved (dimmed in place; the dnd transform shows it). */
  movingWidgetId: string | null;
  /** Commit a resize: new colSpan (snapped) + rowSpan (free). */
  onWidgetResize: (widgetId: string, colSpan: ValidColSpan, rowSpan: number) => void;
  /** Render a single widget's body (WidgetRenderer) — passed from ReportCanvas so
   *  CanvasGrid stays presentation-only and doesn't import the whole renderer. */
  renderWidget: (widget: WidgetConfig) => ReactNode;
  /** Selection + chrome callbacks (same as the old WidgetWrapper contract). */
  onWidgetSelect?: (widgetId: string) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onWidgetDuplicate?: (widgetId: string) => void;
}

export function CanvasGrid({
  widgets,
  metrics,
  minHeightPx,
  scale,
  editable,
  selectedWidgetId,
  movingWidgetId,
  onWidgetResize,
  renderWidget,
  onWidgetSelect,
  onWidgetDelete,
  onWidgetDuplicate,
}: CanvasGridProps) {
  return (
    <div style={{ position: "relative", minHeight: minHeightPx }} data-testid="canvas-grid">
      {widgets.map((widget) => (
        <CanvasGridItem
          key={widget.id}
          widget={widget}
          metrics={metrics}
          scale={scale}
          editable={editable}
          isSelected={selectedWidgetId === widget.id}
          isMoving={movingWidgetId === widget.id}
          onWidgetResize={onWidgetResize}
          onWidgetSelect={onWidgetSelect}
          onWidgetDelete={onWidgetDelete}
          onWidgetDuplicate={onWidgetDuplicate}
        >
          {renderWidget(widget)}
        </CanvasGridItem>
      ))}
    </div>
  );
}

interface CanvasGridItemProps {
  widget: WidgetConfig;
  metrics: GridMetrics;
  scale: number;
  editable: boolean;
  isSelected: boolean;
  isMoving: boolean;
  onWidgetResize: (widgetId: string, colSpan: ValidColSpan, rowSpan: number) => void;
  onWidgetSelect?: (widgetId: string) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onWidgetDuplicate?: (widgetId: string) => void;
  children: ReactNode;
}

function CanvasGridItem({
  widget,
  metrics,
  scale,
  editable,
  isSelected,
  isMoving,
  onWidgetResize,
  onWidgetSelect,
  onWidgetDelete,
  onWidgetDuplicate,
  children,
}: CanvasGridItemProps) {
  // Cover-page is pinned (not draggable/resizable) — mirrors RGL's static flag.
  const isStatic = widget.type === "cover-page";
  const draggable = editable && !isStatic;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: widget.id,
    data: { source: "widget" },
    disabled: !draggable,
  });

  // Mount/enter animation: a freshly DROPPED widget eases (scale + fade) from a
  // slightly-smaller state up to its full slot, so the hand-off from the small
  // drag ghost to the full-size canvas widget reads as one continuous object
  // settling — not a hard pop. Flips to "entered" on the next frame after mount.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Live resize preview (px deltas applied locally until pointer-up commits).
  const [resizePreview, setResizePreview] = useState<{ colSpan: number; rowSpan: number } | null>(null);
  const resizeStateRef = useRef<{ startX: number; startY: number } | null>(null);

  const baseRect = cellToPixelRect(
    widget.position,
    { colSpan: resizePreview?.colSpan ?? widget.position.colSpan, rowSpan: resizePreview?.rowSpan ?? widget.position.rowSpan ?? 1 },
    metrics,
  );

  // dnd-kit's transform is in SCREEN px; the grid is scaled, so divide by scale to
  // keep the dragged element under the cursor (the on-canvas element is scaled by
  // the parent transform, so its own translate must be in unscaled grid px).
  const dragTranslate = transform
    ? { x: transform.x / (scale > 0 ? scale : 1), y: transform.y / (scale > 0 ? scale : 1) }
    : null;

  // ── Resize (custom SE handle; pointer events, not dnd-kit) ──────────────
  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizeStateRef.current = { startX: e.clientX, startY: e.clientY };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );
  const onResizePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const start = resizeStateRef.current;
      if (!start) return;
      // Guard against a stale resize state: if no button is pressed this is a pure
      // HOVER (the prior drag's pointerup never reached us — e.g. it ended off the
      // handle, or the handle re-rendered mid-drag). Without this, hovering the SE
      // corner would resize on its own. Clear the state and bail.
      if (e.buttons === 0) {
        resizeStateRef.current = null;
        setResizePreview(null);
        return;
      }
      const delta = { x: e.clientX - start.startX, y: e.clientY - start.startY };
      const next = applyResizeDelta(
        { colSpan: widget.position.colSpan, rowSpan: widget.position.rowSpan ?? 1 },
        widget.position.col,
        delta,
        scale,
        metrics,
        snapToValidSpan,
      );
      setResizePreview(next);
    },
    [widget.position.colSpan, widget.position.rowSpan, widget.position.col, scale, metrics],
  );
  const onResizePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const start = resizeStateRef.current;
      resizeStateRef.current = null;
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
      if (start && resizePreview) {
        onWidgetResize(widget.id, resizePreview.colSpan as ValidColSpan, resizePreview.rowSpan);
      }
      setResizePreview(null);
    },
    [resizePreview, onWidgetResize, widget.id],
  );
  // Abnormal end (pointercancel / capture lost): drop the resize state so a later
  // hover can't resume a half-finished resize.
  const onResizePointerCancel = useCallback(() => {
    resizeStateRef.current = null;
    setResizePreview(null);
  }, []);

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  // Compose OUR click-vs-drag tracking with dnd-kit's own onPointerDown (which
  // starts the drag). We must NOT replace dnd-kit's handler — doing so meant the
  // draggable lost its pointer listener after the first move and "wouldn't drag
  // again" (the listeners object is re-created each render; an overriding
  // onPointerDown silently swallows dnd-kit's). So we record the start position
  // AND forward the event to dnd-kit's listener.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
      if (draggable) (listeners as Record<string, ((e: React.PointerEvent) => void) | undefined>)?.onPointerDown?.(e);
    },
    [draggable, listeners],
  );
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Click-vs-drag: ignore if the pointer moved (a drag, not a select).
      if (pointerDownPos.current) {
        const dx = Math.abs(e.clientX - pointerDownPos.current.x);
        const dy = Math.abs(e.clientY - pointerDownPos.current.y);
        pointerDownPos.current = null;
        if (dx > 8 || dy > 8) return;
      }
      if (editable && onWidgetSelect) {
        e.stopPropagation();
        onWidgetSelect(widget.id);
      }
    },
    [editable, onWidgetSelect, widget.id],
  );

  // Per-widget appearance (Appearance section in the editor). Applied to the
  // shared widget card here AND mirrored by the server PDF renderer (parity).
  const wStyle = (widget.config?.style ?? {}) as {
    fillColor?: string;
    borderColor?: string;
    borderRadius?: number;
    textColor?: string;
    fontFamily?: string;
  };
  const DEFAULT_BORDER = "var(--hairline)";
  const HOVER_BORDER = "hsl(214 25% 55%)";
  const restingBorder = wStyle.borderColor ?? DEFAULT_BORDER;

  return (
    <div
      ref={setNodeRef}
      data-testid={`grid-cell-${widget.id}`}
      style={{
        position: "absolute",
        left: baseRect.left,
        top: baseRect.top,
        width: baseRect.width,
        height: baseRect.height,
        transform: dragTranslate
          ? `translate(${dragTranslate.x}px, ${dragTranslate.y}px)`
          : entered
            ? "none"
            : "scale(0.96)",
        // Glide to the snapped slot instead of teleporting — a widget eases into
        // its grid position on drop/move/resize. Disabled WHILE dragging so the
        // widget tracks the cursor 1:1 (no lag), then the transition takes over on
        // release to settle it smoothly. Also a gentle scale/opacity on first
        // mount so a dropped widget appears rather than pops.
        transition: isDragging
          ? "none"
          : "left 0.35s cubic-bezier(0.2,0,0,1), top 0.35s cubic-bezier(0.2,0,0,1), width 0.35s cubic-bezier(0.2,0,0,1), height 0.35s cubic-bezier(0.2,0,0,1), transform 0.35s cubic-bezier(0.2,0,0,1), opacity 0.3s ease",
        zIndex: isDragging ? 50 : isSelected ? 20 : 1,
        opacity: isMoving && !isDragging ? 0.4 : entered ? 1 : 0,
        touchAction: draggable ? "none" : undefined,
        cursor: draggable ? (isDragging ? "grabbing" : "grab") : "default",
      }}
      {...(draggable ? attributes : {})}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <div
        data-testid={`widget-wrapper-${widget.id}`}
        // Border + radius matched to the clients-page table (ClientTableView):
        // 0.5px solid var(--hairline), rounded-[22px], soft shadow. Selection adds
        // NO colour/ring (per design) — the floating action bar above the widget is
        // the only selected affordance. Hover deepens the border slightly.
        className="relative h-full min-w-0 overflow-hidden rounded-[22px] bg-background shadow-xs"
        style={{
          border: `0.5px solid ${restingBorder}`,
          ...(wStyle.fillColor ? { backgroundColor: wStyle.fillColor } : {}),
          ...(wStyle.borderRadius != null ? { borderRadius: wStyle.borderRadius } : {}),
          ...(wStyle.textColor ? { color: wStyle.textColor } : {}),
          ...(wStyle.fontFamily ? { fontFamily: wStyle.fontFamily } : {}),
        }}
        onMouseEnter={(e) => {
          // Don't deepen a custom border colour on hover — keep what the user chose.
          if (editable && !isSelected && !wStyle.borderColor) e.currentTarget.style.border = `0.5px solid ${HOVER_BORDER}`;
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.border = `0.5px solid ${restingBorder}`;
        }}
        aria-label={`Widget: ${widget.title}`}
        aria-selected={isSelected}
      >
        {children}

        {/* SE resize handle — selected, editable, non-static widgets only. */}
        {editable && isSelected && !isStatic && (
          <div
            data-testid={`resize-handle-${widget.id}`}
            onPointerDown={onResizePointerDown}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onPointerCancel={onResizePointerCancel}
            onLostPointerCapture={onResizePointerCancel}
            className="absolute z-20"
            style={{
              right: -4,
              bottom: -4,
              width: 14,
              height: 14,
              borderRadius: 4,
              background: "hsl(214 25% 55%)",
              border: "2px solid hsl(0 0% 100%)",
              cursor: "nwse-resize",
              touchAction: "none",
            }}
            aria-label={`Resize ${widget.title}`}
          />
        )}
      </div>

      {/* Floating action bar — duplicate + delete, hovering ABOVE the widget when
          selected. Rendered OUTSIDE the overflow-hidden inner wrapper (as a sibling
          in the absolute cell) so it isn't clipped. Icons match the sidebar/toolbar
          ghost-icon design: monochrome, rounded-lg, hover bg, in a white pill. */}
      {editable && isSelected && (
        <div
          data-testid={`widget-actions-${widget.id}`}
          className="absolute right-0 bottom-full mb-1.5 z-30 flex items-center gap-0.5 rounded-xl bg-background p-0.5 shadow-md"
          style={{ border: "0.5px solid var(--hairline)" }}
          // Don't start a widget drag / deselect from the action bar.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {onWidgetDuplicate && (
            <button
              data-testid={`duplicate-btn-${widget.id}`}
              onClick={(e) => { e.stopPropagation(); onWidgetDuplicate(widget.id); }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-foreground hover:bg-accent/50 cursor-pointer transition-colors"
              aria-label={`Duplicate ${widget.title}`}
              type="button"
            >
              <Copy size={15} />
            </button>
          )}
          {onWidgetDelete && (
            <button
              data-testid={`delete-btn-${widget.id}`}
              onClick={(e) => { e.stopPropagation(); onWidgetDelete(widget.id); }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-foreground hover:bg-accent/50 cursor-pointer transition-colors"
              aria-label={`Delete ${widget.title}`}
              type="button"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
